/**
 * Microsoft admin — agency-level credentials for the Bing Webmaster
 * Tools API (and, optionally, Microsoft Graph / Microsoft Advertising
 * via Azure AD).
 *
 * Mirrors `GoogleIntegrationsPage.js`: this page only holds the
 * agency-wide app credentials. Per-client Bing connections live on
 * each client's own integration tab and reuse the OAuth client_id /
 * secret saved here.
 *
 * IMPORTANT: Bing Webmaster Tools has its **own** OAuth server (not
 * Azure AD). Register the OAuth client at
 *   https://www.bing.com/webmasters → Settings → API Access → OAuth
 * The Azure tenant/client fields are reserved for later Microsoft
 * Graph or Microsoft Advertising hooks — they're not used by Bing.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import {
  Save, ExternalLink, Eye, EyeOff, Users, Key, Loader2, CheckCircle2,
  AlertTriangle,
} from "lucide-react";

// Tiny inline Microsoft 4-square logo (Office-ish quadrants).
function MicrosoftLogo({ className }) {
  return (
    <svg viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg"
      className={className} aria-hidden="true">
      <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export default function MicrosoftIntegrationsPage() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [form, setForm] = useState({
    bing_oauth_client_id: "",
    bing_oauth_client_secret: "",
    bing_api_key: "",
    azure_tenant_id: "",
    azure_client_id: "",
    azure_client_secret: "",
    redirect_uri: "",
  });
  const [showBingSecret, setShowBingSecret] = useState(false);
  const [showBingApiKey, setShowBingApiKey] = useState(false);
  const [showAzureSecret, setShowAzureSecret] = useState(false);

  // Default callback path matches the backend OAuth route we'll register.
  const defaultRedirect =
    `${window.location.origin.replace(
      /^https?:\/\/[^/]+/,
      process.env.REACT_APP_BACKEND_URL,
    )}/api/microsoft/oauth/callback`;

  const refresh = async () => {
    try {
      const { data } = await api.get("/integrations/microsoft/settings");
      setSettings(data);
      setForm((f) => ({
        ...f,
        bing_oauth_client_id: data.bing_oauth_client_id || "",
        azure_tenant_id: data.azure_tenant_id || "",
        azure_client_id: data.azure_client_id || "",
        redirect_uri: data.redirect_uri || defaultRedirect,
      }));
    } catch (e) {
      logger.warn("MicrosoftIntegrations: load settings failed", e);
      setSettings({});
      setForm((f) => ({ ...f, redirect_uri: defaultRedirect }));
    }
  };

  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const body = { ...form };
      // Only send secrets when actually typed — keeps stored values intact.
      if (!body.bing_oauth_client_secret) delete body.bing_oauth_client_secret;
      if (!body.bing_api_key) delete body.bing_api_key;
      if (!body.azure_client_secret) delete body.azure_client_secret;
      await api.put("/integrations/microsoft/settings", body);
      toast.success("Microsoft credentials saved");
      setForm((f) => ({
        ...f,
        bing_oauth_client_secret: "",
        bing_api_key: "",
        azure_client_secret: "",
      }));
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/integrations/microsoft/test");
      setTestResult(data);
      if (data.ok) {
        toast.success(`Bing reachable — ${data.count ?? "?"} site(s) visible.`);
      } else {
        toast.error(
          `Bing call returned ${data.status_code || "an error"}: `
          + (typeof data.body === "object" ? JSON.stringify(data.body).slice(0, 120) : (data.body || data.error || "Unknown")),
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test call failed");
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  }

  const fieldClass = "rounded-sm font-mono text-xs h-10";
  const bingConfigured = !!(settings.bing_oauth_client_id && settings.has_bing_client_secret)
    || !!settings.has_bing_api_key;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="microsoft-admin-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-white border border-zinc-200">
          <MicrosoftLogo className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <div className="label-overline">Integrations</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">
            Microsoft admin
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Agency-level credentials for <strong>Bing Webmaster Tools</strong>
            {" "}(search performance, queries, pages, crawl stats — the Bing
            equivalent of Google Search Console) plus reserved slots for
            Microsoft Graph and Microsoft Advertising hooks. Save these once
            and every per-client Bing connection will reuse them.
          </p>
        </div>
      </div>

      {/* Per-client banner */}
      <div className="rounded-sm border border-blue-200 bg-blue-50 p-4 flex items-start gap-3"
        data-testid="microsoft-per-client-banner">
        <Users className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-900">
          <div className="font-semibold">Bing connections live per-client</div>
          <div className="text-[12px] mt-1 leading-relaxed">
            Once these credentials are saved, every client will get a
            &quot;Connect Bing Webmaster&quot; button on their dedicated Bing
            feeds page. Each grant scopes the client&apos;s verified sites
            exclusively to that client. This page only holds the
            agency-wide app credentials.
          </div>
        </div>
      </div>

      {/* Bing Webmaster Tools card */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-bold text-xl flex items-center gap-2">
                <span className="h-6 w-6 grid place-items-center rounded-sm bg-[#0078D4]/10">
                  <Key className="h-3.5 w-3.5 text-[#0078D4]" />
                </span>
                Bing Webmaster Tools
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Register the OAuth client at{" "}
                <a href="https://www.bing.com/webmasters/home"
                  target="_blank" rel="noreferrer" className="underline">
                  bing.com/webmasters
                  <ExternalLink className="inline h-3 w-3 ml-0.5" />
                </a>{" "}
                → Settings → <span className="font-mono">API Access</span>{" "}
                → OAuth. The Bing OAuth server is separate from Azure AD —
                its scopes don&apos;t appear in the Microsoft Identity portal.
              </p>
            </div>
            <Badge className={`rounded-sm ${bingConfigured
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-amber-100 text-amber-800 border-amber-200"}`}
              data-testid="microsoft-bing-config-badge">
              {bingConfigured ? "Configured" : "Credentials required"}
            </Badge>
          </div>

          <form onSubmit={save} className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-overline">Bing OAuth client ID</Label>
              <Input value={form.bing_oauth_client_id}
                onChange={(e) => setForm((f) => ({ ...f, bing_oauth_client_id: e.target.value }))}
                className={fieldClass}
                placeholder="bing-webmaster-client-id"
                data-testid="microsoft-bing-client-id" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">
                Bing OAuth client secret {settings.has_bing_client_secret && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· stored</span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input type={showBingSecret ? "text" : "password"}
                  value={form.bing_oauth_client_secret}
                  onChange={(e) => setForm((f) => ({ ...f, bing_oauth_client_secret: e.target.value }))}
                  placeholder={settings.has_bing_client_secret
                    ? "•••••••• (leave blank to keep)"
                    : "Paste client secret"}
                  className={`${fieldClass} flex-1`}
                  data-testid="microsoft-bing-client-secret" />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setShowBingSecret((s) => !s)}>
                  {showBingSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">
                Bing API key (optional fallback)
                {settings.has_bing_api_key && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· stored</span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input type={showBingApiKey ? "text" : "password"}
                  value={form.bing_api_key}
                  onChange={(e) => setForm((f) => ({ ...f, bing_api_key: e.target.value }))}
                  placeholder={settings.has_bing_api_key
                    ? "•••••••• (leave blank to keep)"
                    : "Optional — paste the API key for the agency's master Bing account"}
                  className={`${fieldClass} flex-1`}
                  data-testid="microsoft-bing-api-key" />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setShowBingApiKey((s) => !s)}>
                  {showBingApiKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <p className="text-[11px] text-zinc-500">
                Bing supports two auth modes — OAuth (per-user delegated
                access) and API key (single-account access). Drop in the
                agency&apos;s master API key for an immediate test path
                while per-client OAuth is being wired up.
              </p>
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">Authorised redirect URI (paste into Bing OAuth registration)</Label>
              <Input value={form.redirect_uri} readOnly
                className="rounded-sm font-mono text-[11px] h-9 bg-zinc-50"
                onFocus={(e) => e.target.select()}
                data-testid="microsoft-redirect-uri" />
              <p className="text-[11px] text-zinc-500">
                Bing requires an exact (case-sensitive, trailing-slash-sensitive)
                match. Add this URI as the only registered redirect for the
                OAuth client.
              </p>
            </div>

            {/* Save + Test row */}
            <div className="md:col-span-2 flex justify-between flex-wrap gap-2">
              <Button type="button" variant="outline"
                className="rounded-sm"
                disabled={testing || !settings.has_bing_api_key}
                onClick={runTest}
                title={settings.has_bing_api_key
                  ? "Calls GetSites with the saved API key"
                  : "Save a Bing API key first"}
                data-testid="microsoft-test-bing">
                {testing
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Test Bing connection
              </Button>
              <Button type="submit" disabled={busy}
                className="rounded-sm bg-[#0078D4] hover:bg-[#106EBE] text-white"
                data-testid="microsoft-save-settings">
                <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save credentials"}
              </Button>
            </div>

            {testResult && (
              <div className={`md:col-span-2 rounded-sm border p-3 text-[12px] ${
                testResult.ok
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-200 bg-red-50 text-red-900"
              }`} data-testid="microsoft-test-result">
                <div className="flex items-start gap-2">
                  {testResult.ok
                    ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
                  <div className="flex-1 space-y-2 min-w-0">
                    {testResult.ok ? (
                      <div className="text-sm">
                        <strong>Bing reachable.</strong>{" "}
                        Found <strong>{testResult.count ?? 0}</strong> verified site(s) on the master account.
                      </div>
                    ) : (
                      <>
                        <div className="text-sm font-semibold">
                          Bing rejected the call
                          {testResult.status_code ? ` (HTTP ${testResult.status_code})` : ""}.
                        </div>
                        {testResult.hint && (
                          <div className="text-[12px] leading-relaxed">
                            {testResult.hint}
                          </div>
                        )}
                      </>
                    )}
                    <details className="text-[10px] font-mono text-zinc-600">
                      <summary className="cursor-pointer select-none">
                        Raw response
                      </summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all">
                        {JSON.stringify(testResult, null, 2)}
                      </pre>
                    </details>
                  </div>
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Azure AD card (reserved for future Microsoft Graph / Advertising) */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4 opacity-95">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-bold text-xl flex items-center gap-2">
                <span className="h-6 w-6 grid place-items-center rounded-sm bg-[#5E5E5E]/10">
                  <MicrosoftLogo className="h-3.5 w-3.5" />
                </span>
                Azure AD app (reserved)
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Optional. Used later for Microsoft Graph (Outlook / Calendar /
                Teams journalling) and Microsoft Advertising (Bing Ads spend
                reporting). Register an app at{" "}
                <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps"
                  target="_blank" rel="noreferrer" className="underline">
                  portal.azure.com → App registrations
                  <ExternalLink className="inline h-3 w-3 ml-0.5" />
                </a>.
              </p>
            </div>
          </div>

          <form onSubmit={save} className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-overline">Azure tenant ID</Label>
              <Input value={form.azure_tenant_id}
                onChange={(e) => setForm((f) => ({ ...f, azure_tenant_id: e.target.value }))}
                className={fieldClass}
                placeholder="00000000-0000-0000-0000-000000000000 (or 'common')"
                data-testid="microsoft-azure-tenant" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Azure client ID</Label>
              <Input value={form.azure_client_id}
                onChange={(e) => setForm((f) => ({ ...f, azure_client_id: e.target.value }))}
                className={fieldClass}
                placeholder="Azure app registration client_id"
                data-testid="microsoft-azure-client" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">
                Azure client secret {settings.has_azure_client_secret && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· stored</span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input type={showAzureSecret ? "text" : "password"}
                  value={form.azure_client_secret}
                  onChange={(e) => setForm((f) => ({ ...f, azure_client_secret: e.target.value }))}
                  placeholder={settings.has_azure_client_secret
                    ? "•••••••• (leave blank to keep)"
                    : "Paste Azure client secret"}
                  className={`${fieldClass} flex-1`}
                  data-testid="microsoft-azure-secret" />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setShowAzureSecret((s) => !s)}>
                  {showAzureSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
